import { Controller } from "@hotwired/stimulus"

// Step 3 of the consultation wizard · "variante B" availability editor.
//
// Source of truth = a JS array `state[wday]` of [from, to] tuples (strings
// "HH:MM"). The controller re-renders one row per weekday (Mon → Sun) and
// keeps the hidden inputs `consultation[availability][1..6,0]` synced as
// comma-joined "09:00-12:00,14:00-18:00" strings — exactly the format
// expected by the `ConsultationAvailability` concern.
//
// Markup contract:
//   <div data-controller="consult-slots"
//        data-consult-slots-initial-value='{"1":"09:00-12:00,14:00-18:00", ...}'
//        data-consult-slots-duration-value="30">
//     <div data-consult-slots-target="hiddenInputs"></div>
//     <div data-consult-slots-target="rows"></div>
//     <div data-consult-slots-target="summary"></div>
//   </div>
const DAYS = [
  { wday: 1, label: "Lundi"    },
  { wday: 2, label: "Mardi"    },
  { wday: 3, label: "Mercredi" },
  { wday: 4, label: "Jeudi"    },
  { wday: 5, label: "Vendredi" },
  { wday: 6, label: "Samedi"   },
  { wday: 0, label: "Dimanche" }
]

export default class extends Controller {
  static targets = ["hiddenInputs", "rows", "summary"]
  static values  = { initial: Object, duration: { type: Number, default: 30 } }

  connect() {
    this.state = {}
    DAYS.forEach(({ wday }) => {
      this.state[wday] = this.#parse(this.initialValue?.[String(wday)] || "")
    })
    this.#renderAll()
  }

  // ── Public actions (referenced from the view via data-action) ──────

  toggleDay(event) {
    const wday = Number(event.currentTarget.dataset.wday)
    if (this.state[wday].length > 0) {
      this.state[wday] = []
    } else {
      this.state[wday] = [["09:00", "12:00"], ["14:00", "18:00"]]
    }
    this.#renderAll()
  }

  addRange(event) {
    const wday = Number(event.currentTarget.dataset.wday)
    // Default to a sensible new slot; if the day already has ranges,
    // start right after the last one (capped at 22:00).
    const existing = this.state[wday]
    const lastEnd  = existing.length ? existing[existing.length - 1][1] : "09:00"
    const newFrom  = this.#addHours(lastEnd, 1)
    const newTo    = this.#addHours(newFrom, 2)
    this.state[wday].push([newFrom, newTo])
    this.#renderAll()
  }

  removeRange(event) {
    const wday  = Number(event.currentTarget.dataset.wday)
    const index = Number(event.currentTarget.dataset.index)
    this.state[wday].splice(index, 1)
    this.#renderAll()
  }

  updateRange(event) {
    const wday  = Number(event.currentTarget.dataset.wday)
    const index = Number(event.currentTarget.dataset.index)
    const slot  = Number(event.currentTarget.dataset.slot) // 0 = from, 1 = to
    const value = event.currentTarget.value
    if (!this.state[wday][index]) return
    this.state[wday][index][slot] = value
    this.#renderHidden()
    this.#renderSummary()
  }

  applyPreset(event) {
    const preset = event.currentTarget.dataset.preset
    switch (preset) {
      case "weekdays-morning":
        [1, 2, 3, 4, 5].forEach(w => this.#mergeRange(w, "09:00", "12:00"))
        break
      case "weekdays-afternoon":
        [1, 2, 3, 4, 5].forEach(w => this.#mergeRange(w, "14:00", "18:00"))
        break
      case "evening":
        [1, 2, 3, 4, 5].forEach(w => this.#mergeRange(w, "18:00", "21:00"))
        break
      case "saturday-morning":
        this.#mergeRange(6, "09:00", "12:00")
        break
      case "clear":
        DAYS.forEach(({ wday }) => { this.state[wday] = [] })
        break
    }
    this.#renderAll()
  }

  copyToAll(event) {
    const wday   = Number(event.currentTarget.dataset.wday)
    const source = this.state[wday]
    DAYS.forEach(({ wday: target }) => {
      if (target === wday) return
      this.state[target] = source.map(([f, t]) => [f, t])
    })
    this.#renderAll()
  }

  // ── Rendering ──────────────────────────────────────────────────────

  #renderAll() {
    this.#renderRows()
    this.#renderHidden()
    this.#renderSummary()
  }

  #renderRows() {
    this.rowsTarget.replaceChildren()
    DAYS.forEach(({ wday, label }) => {
      this.rowsTarget.appendChild(this.#dayRow(wday, label))
    })
  }

  #dayRow(wday, label) {
    const ranges = this.state[wday] || []
    const isOn   = ranges.length > 0

    const row = document.createElement("div")
    row.className = "slots-day-row" + (isOn ? "" : " is-off")

    // Toggle
    const toggleWrap = document.createElement("button")
    toggleWrap.type = "button"
    toggleWrap.className = "slots-day-toggle"
    toggleWrap.dataset.wday = wday
    toggleWrap.dataset.action = "click->consult-slots#toggleDay"
    const switchEl = document.createElement("span")
    switchEl.className = "slots-switch" + (isOn ? " is-on" : "")
    const switchKnob = document.createElement("span")
    switchKnob.className = "slots-switch__knob"
    switchEl.appendChild(switchKnob)
    const labelEl = document.createElement("span")
    labelEl.className = "slots-day-label"
    labelEl.textContent = label
    toggleWrap.append(switchEl, labelEl)
    row.appendChild(toggleWrap)

    // Ranges area
    const rangesEl = document.createElement("div")
    rangesEl.className = "slots-ranges"
    if (!isOn) {
      const off = document.createElement("span")
      off.className = "slots-off-label"
      off.textContent = "Indisponible"
      rangesEl.appendChild(off)
    } else {
      ranges.forEach(([from, to], idx) => {
        rangesEl.appendChild(this.#rangeChip(wday, idx, from, to))
      })
    }
    row.appendChild(rangesEl)

    // Actions
    const actions = document.createElement("div")
    actions.className = "slots-day-actions"
    if (isOn) {
      const addBtn = document.createElement("button")
      addBtn.type = "button"
      addBtn.className = "btn btn-sm btn-ghost"
      addBtn.dataset.wday   = wday
      addBtn.dataset.action = "click->consult-slots#addRange"
      addBtn.textContent = "+ Plage"
      actions.appendChild(addBtn)

      const copyBtn = document.createElement("button")
      copyBtn.type = "button"
      copyBtn.className = "btn btn-sm btn-ghost"
      copyBtn.dataset.wday   = wday
      copyBtn.dataset.action = "click->consult-slots#copyToAll"
      copyBtn.textContent = "Copier vers tous"
      actions.appendChild(copyBtn)
    }
    row.appendChild(actions)

    return row
  }

  #rangeChip(wday, index, from, to) {
    const chip = document.createElement("div")
    chip.className = "slots-range"

    const inputFrom = document.createElement("input")
    inputFrom.type = "time"
    inputFrom.value = from
    inputFrom.className = "slots-range__time"
    inputFrom.dataset.wday   = wday
    inputFrom.dataset.index  = index
    inputFrom.dataset.slot   = 0
    inputFrom.dataset.action = "change->consult-slots#updateRange"
    chip.appendChild(inputFrom)

    const arrow = document.createElement("span")
    arrow.className = "slots-range__arrow"
    arrow.textContent = "→"
    chip.appendChild(arrow)

    const inputTo = document.createElement("input")
    inputTo.type = "time"
    inputTo.value = to
    inputTo.className = "slots-range__time"
    inputTo.dataset.wday   = wday
    inputTo.dataset.index  = index
    inputTo.dataset.slot   = 1
    inputTo.dataset.action = "change->consult-slots#updateRange"
    chip.appendChild(inputTo)

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "slots-range__remove"
    removeBtn.setAttribute("aria-label", "Retirer cette plage")
    removeBtn.dataset.wday   = wday
    removeBtn.dataset.index  = index
    removeBtn.dataset.action = "click->consult-slots#removeRange"
    removeBtn.textContent = "✕"
    chip.appendChild(removeBtn)

    return chip
  }

  #renderHidden() {
    this.hiddenInputsTarget.replaceChildren()
    DAYS.forEach(({ wday }) => {
      const serialized = this.#serializeDay(wday)
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = `consultation[availability][${wday}]`
      input.value = serialized
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  #renderSummary() {
    if (!this.hasSummaryTarget) return
    const totalMinutes = DAYS.reduce((sum, { wday }) => {
      return sum + this.state[wday].reduce((s, [f, t]) => s + this.#diff(f, t), 0)
    }, 0)
    const hours      = Math.round(totalMinutes / 60)
    // Plage par plage, comme ConsultationAvailability#upcoming_slots : trois
    // plages de 3 h ne font aucune séance de 4 h, même si la semaine en
    // compte 15 (le total donnait « 3 créneaux », 20/10/2026).
    const slotCount  = this.durationValue > 0
      ? DAYS.reduce((sum, { wday }) => sum + this.state[wday].reduce(
          (s, [f, t]) => s + Math.floor(this.#diff(f, t) / this.durationValue), 0), 0)
      : 0
    const monthlyEst = slotCount * 4
    // 20/10/2026 : deux lignes posées l'une sous l'autre, jamais un « · »
    // ou un « — » qui pourrait ouvrir une ligne au repli. Plus de « RDV »,
    // et la durée se dit comme ailleurs (« 4 h », pas « 240 min »).
    const nbsp = " "
    const lignes = [this.#summaryNode(`${hours}${nbsp}h de disponibilité par semaine`, "strong")]
    if (slotCount > 0) {
      lignes.push(this.#summaryNode(
        `Environ ${slotCount}${nbsp}créneau${slotCount > 1 ? "x" : ""} de ${this.#dureeEnClair(this.durationValue)}, ` +
        `soit près de ${monthlyEst}${nbsp}séance${monthlyEst > 1 ? "s" : ""} par mois`, "span"))
    } else if (totalMinutes > 0) {
      lignes.push(this.#summaryNode(
        `Aucune plage n'est assez longue pour une séance de ${this.#dureeEnClair(this.durationValue)} : ` +
        `allongez une plage ou raccourcissez la séance.`, "span"))
    }
    this.summaryTarget.replaceChildren(...lignes)
  }

  #dureeEnClair(minutes) {
    const h = Math.floor(minutes / 60), m = minutes % 60
    if (h === 0) return `${m} min`
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`
  }

  #summaryNode(text, tag) {
    const el = document.createElement(tag)
    el.textContent = text
    return el
  }

  // ── Helpers ────────────────────────────────────────────────────────

  #parse(str) {
    if (!str) return []
    return String(str).split(",").map(chunk => {
      const m = chunk.trim().match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/)
      if (!m) return null
      return [`${m[1].padStart(2, "0")}:${m[2]}`, `${m[3].padStart(2, "0")}:${m[4]}`]
    }).filter(Boolean)
  }

  #serializeDay(wday) {
    return (this.state[wday] || [])
      .filter(([f, t]) => f && t && this.#diff(f, t) > 0)
      .map(([f, t]) => `${f}-${t}`)
      .join(",")
  }

  #diff(from, to) {
    const a = this.#toMinutes(from)
    const b = this.#toMinutes(to)
    return Math.max(0, b - a)
  }

  #toMinutes(hhmm) {
    const [h, m] = String(hhmm).split(":").map(Number)
    return (h || 0) * 60 + (m || 0)
  }

  #addHours(hhmm, hours) {
    let m = this.#toMinutes(hhmm) + hours * 60
    m = Math.min(m, 22 * 60)
    const h  = Math.floor(m / 60)
    const mm = m % 60
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
  }

  #mergeRange(wday, from, to) {
    // Avoid duplicating a preset range if it's already there. Naive
    // equality is fine — presets are exact-match strings.
    const exists = this.state[wday].some(([f, t]) => f === from && t === to)
    if (!exists) this.state[wday].push([from, to])
  }
}
